// A keyboard-wedge barcode scanner types like a keyboard, only faster: each character a few milliseconds after the one before,
// a capital as Shift and the letter, and Enter at the end. A person's keys are tens of milliseconds apart at the least. This
// module tells the two apart, so a scan never reaches a shortcut — the shell's Shift+L, `g t`, `/` and `?`, a list's N and Enter
// (the review of 18 Oct 2026, D1-2; TC-IDENTITY-109) — and so a screen can take a scan that lands while the focus is on a button
// or nowhere, as if it had been typed into its scan box (TC-INVENTORY-141, TC-INVENTORY-151) — or in a field that takes a figure,
// such as a receipt line's quantity (TC-PURCHASING-125).

/** Keys this close together, a character or Shift after a character or Shift, come from a scanner (UC-INVENTORY-008, CAT-62). */
export const BURST_GAP_MS = 30;
/** The shortest code taken as a scan outside a scan box: a person pressing a key then Enter never makes one. */
export const MIN_SCAN_LENGTH = 3;

/** A key that can belong to a scanned code: a printable character, or the Shift a scanner sends before a capital. */
const scanKey = (e: KeyboardEvent): boolean => e.key.length === 1 || e.key === "Shift";

let lastEvent: Event | null = null;
let lastAt = Number.NEGATIVE_INFINITY;
let prevAt = Number.NEGATIVE_INFINITY;

/** Notes when each scan key went down; repeats (a key held) are not new keys. Safe to call more than once for one event. */
function track(e: KeyboardEvent): void {
  if (e === lastEvent) return;
  lastEvent = e;
  if (e.repeat || !scanKey(e)) return;
  prevAt = lastAt;
  lastAt = e.timeStamp;
}

if (typeof window !== "undefined") window.addEventListener("keydown", track, true);

/**
 * Whether this key arrived within a scanner's burst: a few milliseconds after the character (or Shift) before it. The first key
 * of a scan is told apart only when it is a capital, whose Shift comes first; a scanned code that begins with a lowercase
 * shortcut key is not, and a code is rarely so.
 */
export function inBurst(e: KeyboardEvent): boolean {
  track(e);
  const before = e === lastEvent && scanKey(e) && !e.repeat ? prevAt : lastAt;
  return e.timeStamp - before < BURST_GAP_MS;
}

/** Whether the key went to a field that takes typing: such a key is the field's, never a shortcut or a stray scan. */
export const isTypingTarget = (t: EventTarget | null): boolean =>
  t instanceof HTMLElement && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT" || t.isContentEditable);

/**
 * Gathers the characters of a scan that lands outside every field — the focus on a button, a row or nothing — and answers the
 * code when the scanner's Enter ends it. Feed it every keydown that reached no field; anything slower than a scanner starts over.
 */
export function scanCollector(): { take: (e: KeyboardEvent) => string | null } {
  const field = fieldScanCollector();
  return { take: (e) => field.take(e, "")?.code ?? null };
}

/**
 * Watches one field that takes a figure — a line's quantity or unit cost — for a scan that lands in it: the scanner's keys a few
 * milliseconds apart and its Enter at the end. Feed it each keydown of the field with the field's value as it stood before the key.
 * When the scanner's Enter ends a code it answers the code and the value the field held before the code began, so the screen can
 * put the field back and take the code as a scan. A person's keys, slower, answer nothing, nor does a person's Enter after them:
 * a figure typed acts as ever (UC-PURCHASING-005 A19; TC-PURCHASING-125).
 */
export function fieldScanCollector(): { take: (e: KeyboardEvent, value: string) => { code: string; before: string } | null } {
  let code = "";
  let before = "";
  let at = Number.NEGATIVE_INFINITY;
  return {
    take(e, value) {
      if (e.repeat) return null;
      const close = e.timeStamp - at < BURST_GAP_MS;
      if (e.key === "Enter") {
        const done = close && code.length >= MIN_SCAN_LENGTH ? { code, before } : null;
        code = "";
        at = Number.NEGATIVE_INFINITY;
        return done;
      }
      if (e.key === "Shift") { if (!close) { code = ""; before = value; } at = e.timeStamp; return null; }
      if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) { code = ""; at = Number.NEGATIVE_INFINITY; return null; }
      if (!close) { code = ""; before = value; }
      code += e.key;
      at = e.timeStamp;
      return null;
    },
  };
}
